
/**
 * Utility functions for page view tracking
 */
import { PageViewEventData, PageViewState } from './types';

/**
 * Gets a cookie value by name
 */
export const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  
  const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return match ? match.pop() || null : null;
};

/**
 * Gets a URL parameter by name
 */
export const getURLParameter = (name: string): string | null => {
  if (typeof window === 'undefined') return null;
  
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(name);
};

/**
 * Gets or creates an external ID for tracking
 */
export const getOrCreateExternalId = (): string => {
  let externalId = localStorage.getItem('tracking_external_id');
  if (!externalId) {
    externalId = `ext_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem('tracking_external_id', externalId);
  }
  return externalId;
};

/**
 * Marks a page view as sent in the global window object
 */
export const markPageViewAsSent = (pathname: string): void => {
  if (typeof window !== 'undefined') {
    // Initialize if not exists
    window._pageViewSent = window._pageViewSent || {};
    window._pageViewSent[pathname] = true;
    
    // Emit a custom event for other components to listen for
    const pageViewEvent = new CustomEvent('pageViewSent', {
      detail: {
        pathname,
        timestamp: new Date().toISOString()
      }
    });
    window.dispatchEvent(pageViewEvent);
    console.log(`📊 Marked PageView as sent for ${pathname} and dispatched event`);
  }
};

/**
 * Checks if a page view has already been sent for the current path
 */
export const hasPageViewBeenSent = (pathname: string): boolean => {
  if (typeof window === 'undefined') return false;
  const result = !!(window._pageViewSent && window._pageViewSent[pathname]);
  console.log(`📊 Checking if PageView was sent for ${pathname}: ${result}`);
  return result;
};

/**
 * Pushes a page view event to the data layer
 */
export const pushToDataLayer = (eventData: PageViewEventData): void => {
  if (typeof window !== 'undefined') {
    // Initialize dataLayer if it doesn't exist
    if (!window.dataLayer) {
      window.dataLayer = [];
      console.log('📊 DataLayer initialized');
    }
    
    window.dataLayer.push({
      event: 'PageView',
      ...eventData
    });
    console.log('✅ PageView pushed to dataLayer:', eventData);
  } else {
    console.log('❌ Window not available, cannot push to dataLayer');
  }
};

/**
 * Ensures the dataLayer is initialized
 */
export const ensureDataLayerInitialized = (): void => {
  if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = [];
    console.log('📊 DataLayer initialized on demand');
  }
};
